import React from 'react';
import { ButtonSecondary, GlyphRight, IconLink } from '@americanexpress/dls-react';
import LiteralsService from 'src/services/literals/LiteralsService';
import { goToSettings } from 'src/services/router.service';
import Selectors from 'src/utils/selectors';
import { useHistory } from 'react-router';
import CalloutGray from 'src/components/atomic/atoms/calloutGray/CalloutGray';
import styles from './linkExternalAccountCallout.module.scss';
import { DLSSize } from 'src/types/dls-size.enum';
import { trackClick } from 'src/hooks/useAdobeTags';
import adobeTags from 'src/types/adobe-tags';
import Anchor from 'src/components/atomic/atoms/anchor/Anchor';
import { BudgetPages } from '../../pages/BudgetPages';
import { useFeatureFlag } from 'src/hooks/queries/one-xp';
import oneXP from 'src/types/one-xp';

export enum LinkExternalAccountCalloutVersions {
    AUTO = 'auto', // Choose automatically based on feature flag value
    CATEGORY_CARDS_REFRESH = 'categoryCardsRefresh',
    ORIGINAL = 'original'
}
export interface LinkExternalAccountCalloutProps {
    version?: LinkExternalAccountCalloutVersions;
}

function Original(): JSX.Element {
    const history = useHistory();
    return (
        <CalloutGray
            heading={LiteralsService.getLiteral('LinkExternalAccountCallout.header')}
            body={LiteralsService.getLiteral('LinkExternalAccountCallout.body')}
            button={
                <ButtonSecondary
                    className={styles.button}
                    size={DLSSize.MEDIUM}
                    data-cy={Selectors.SpendSmart.CALLOUT_BUTTON}
                    onClick={() => {
                        trackClick(history.location.pathname, adobeTags.Callout.LinkAccounts);
                        goToSettings(false, history);
                    }}
                >
                    {LiteralsService.getLiteral('LinkExternalAccountCallout.button')}
                    <GlyphRight className={styles.glyph} />
                </ButtonSecondary>
            }
            icon={
                <IconLink className={styles.icon} size={DLSSize.MEDIUM} data-cy={Selectors.SpendSmart.CALLOUT_ICON} />
            }
            data-cy={Selectors.SpendSmart.EXTERNAL_ACCOUNT_CALLOUT}
        />
    );
}

function CategoryCardsRefreshVersion(): JSX.Element {
    const history = useHistory();
    return (
        <CalloutGray
            heading={LiteralsService.getLiteral('LinkExternalAccountCallout.header')}
            body={
                <>
                    <div>{LiteralsService.getLiteral('LinkExternalAccountCallout.body')}</div>
                    <Anchor
                        href={BudgetPages.SETTINGS}
                        onClick={() => {
                            trackClick(history.location.pathname, adobeTags.Callout.LinkAccounts);
                            goToSettings(false, history);
                        }}
                        data-cy={Selectors.SpendSmart.CALLOUT_ANCHOR}
                    >
                        {LiteralsService.getLiteral('LinkExternalAccountCallout.button')}
                    </Anchor>
                </>
            }
            className="flex flex-column flex-align-start"
            data-cy={Selectors.SpendSmart.EXTERNAL_ACCOUNT_CALLOUT}
            textContainerClassOverrides={styles.gap}
        />
    );
}

function AutoVersion(): JSX.Element {
    const { data: categoryCardsRefreshPhase1FeatureFlag } = useFeatureFlag(
        oneXP.FeatureFlags.CategoryCardsRefreshPhase1
    );
    return (
        <LinkExternalAccountCallout
            version={
                categoryCardsRefreshPhase1FeatureFlag
                    ? LinkExternalAccountCalloutVersions.CATEGORY_CARDS_REFRESH
                    : LinkExternalAccountCalloutVersions.ORIGINAL
            }
        />
    );
}

export default function LinkExternalAccountCallout({
    version = LinkExternalAccountCalloutVersions.AUTO
}: LinkExternalAccountCalloutProps): JSX.Element {
    switch (version) {
        case LinkExternalAccountCalloutVersions.ORIGINAL:
            return <Original />;
        case LinkExternalAccountCalloutVersions.CATEGORY_CARDS_REFRESH:
            return <CategoryCardsRefreshVersion />;
        default:
            return <AutoVersion />;
    }
}
